import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, LinearProgress, Link, Paper, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

export const DIMENSION_NAMES = {
    ut: 'Utterance type (UT)',
    rc: 'Reasoning component (RC)',
};

// A count only, never which items or in what order
export const Progress = ({ done, total, width = 256, complete, noun = 'done' }) => {
    const value = total ? Math.round((done / total) * 100) : 0;
    const finished = complete ?? (total > 0 && done === total);
    return (
        <Box sx={{ width: '100%', maxWidth: width }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                {done} of {total} {noun}
            </Typography>
            <LinearProgress
                variant="determinate"
                value={value}
                aria-label={`${done} of ${total} ${noun}`}
                color={finished ? 'success' : 'primary'}
                sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: 'action.selected',
                    '& .MuiLinearProgress-bar': { borderRadius: 999, transition: 'transform 250ms ease-out' },
                }}
            />
        </Box>
    );
};

// Back link, run name and progress above every item step
export const ItemHeader = ({ run, progress }) => (
    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
        <Box>
            <Link component={RouterLink} to="/adjudicate" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem', fontWeight: 600 }}>
                <ArrowBack sx={{ fontSize: 16 }} /> My queue
            </Link>
            <Typography variant="h3" component="h1" sx={{ mt: 0.5 }}>{run?.name}</Typography>
        </Box>
        {progress && <Progress {...progress} />}
    </Box>
);

// A card with an overline heading, as in the wireframes ("YOUR CODE", "CONTEXT")
export const Panel = ({ title, aside, children, sx, ...rest }) => (
    <Paper component="section" aria-label={typeof title === 'string' ? title : undefined} sx={{ p: { xs: 2, sm: 2.5 }, ...sx }} {...rest}>
        {(title || aside) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
                <Typography variant="overline" component="h2" color="text.secondary">{title}</Typography>
                {aside && <Typography variant="caption" color="text.secondary">{aside}</Typography>}
            </Box>
        )}
        {children}
    </Paper>
);

export const KeysHint = ({ children }) => (
    <Typography variant="caption" component="p" sx={{ mt: 2, color: 'text.disabled', display: { xs: 'none', md: 'block' } }}>
        Keys: {children}
    </Typography>
);

// "Locked 10:42" today, "Locked Dec 9" on another day
export const lockedLabel = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    const today = new Date();
    const sameDay = date.toDateString() === today.toDateString();
    return sameDay
        ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};
